// =============================================================================
// components/RootErrorBoundary.js — Phase 82.8 (shell-level resilience)
//
// The last line of defence. If SectionErrorBoundary is per-tile, this one is
// per-app: it catches anything that escapes the section-level boundaries —
// routing errors, ThemeContext/AuthContext bugs, Shell crashes, malformed
// query params, unexpected component failures — and shows a calm full-page
// fallback instead of a blank white screen.
//
// Behaviors:
//   • Shows a minimal, brand-consistent recovery UI. No stack trace, no raw
//     error string (may contain PII or backend paths).
//   • Offers two escape hatches: "Reload page" (window.location.reload) and
//     "Take me home" (window.location.href = '/dashboard'). Both are hard
//     navigations so client state is fully reset.
//   • Automatically resets when the URL changes (popstate) so back/forward
//     don't remain trapped on the error screen.
//   • Logs to console with a distinctive prefix so incidents can be grepped
//     from support-provided screenshots of DevTools.
//
// Positioning in App.js:
//   Wrap the entire <Router> tree. That way EVERYTHING benefits, including
//   the route matcher, the AuthProvider, ThemeProvider, and Toaster.
// =============================================================================
import React from 'react';

export default class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
    this._onPopState = () => {
      // If the user navigates via browser back/forward while we're showing
      // the error screen, clear the error so the new route can render.
      if (this.state.hasError) this.setState({ hasError: false });
    };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('[RootErrorBoundary]', error, info);
  }

  componentDidMount() {
    window.addEventListener('popstate', this._onPopState);
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this._onPopState);
  }

  handleReload = () => { window.location.reload(); };
  handleHome   = () => { window.location.href = '/dashboard'; };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        className="min-h-screen w-full flex items-center justify-center px-6 py-10 bg-offwhite text-charcoal"
        role="alert"
        data-testid="root-error-boundary"
      >
        <div className="max-w-md w-full text-center space-y-5">
          {/* Wordmark stays consistent with the auth screens — the user should
              still know they're inside 2120 even when things went sideways. */}
          <div className="flex items-center justify-center gap-2">
            <span
              className="inline-block w-7 h-7 rounded-md flex items-center justify-center"
              style={{ background: 'hsl(var(--evergreen))' }}
              aria-hidden="true"
            >
              <span className="font-display text-white text-sm font-semibold">2</span>
            </span>
            <span className="font-display text-lg font-medium tracking-tight">2120</span>
          </div>
          <h1
            className="font-display text-2xl font-medium"
            style={{ letterSpacing: '-0.015em' }}
          >
            Something went off-course.
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            2120 hit an unexpected error while rendering this page. Your data is safe
            &mdash; nothing was written incorrectly. Reload the page or head back home,
            and if this keeps happening please let us know.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 pt-2">
            <button
              type="button"
              onClick={this.handleReload}
              className="px-5 py-2.5 rounded-xl text-sm font-medium bg-evergreen text-white hover:bg-evergreen-dark transition-colors"
              data-testid="root-error-reload"
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              className="px-5 py-2.5 rounded-xl text-sm font-medium border border-evergreen/30 text-evergreen hover:bg-evergreen/5 transition-colors"
              data-testid="root-error-home"
            >
              Take me home
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground/70 pt-4">
            Trouble persists?{' '}
            <a
              href="mailto:hello@2120.money?subject=2120%20error"
              className="underline hover:text-charcoal transition-colors"
            >
              Reach out
            </a>{' '}
            and we&apos;ll investigate.
          </p>
        </div>
      </div>
    );
  }
}
